import { extractImageDelivery, resolveThumbnailUrl } from "./imageVariants";
import { resolvePublicGalleryItems } from "./publicGalleryContract";
import { resolvePublicMediaContract } from "./publicMediaContract";

const STORAGE_KEY = "ecoTourRecentlyVisited";
const MAX_ITEMS = 12;
const UPDATED_EVENT = "ecotour:recently-visited";
const RECENT_IMAGE_SLOT = "entity_medium_thumb_desktop";

const notifyRecentlyVisitedChanged = (detail = {}) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail }));
};

const resolveImageValue = (value) => {
  return resolveThumbnailUrl(value, "mid", true) || "";
};

const resolveCanonicalRecentSource = (value) => {
  if (!value || typeof value !== "object") return value;
  const publicMedia = resolvePublicMediaContract(value);
  const publishedItems = resolvePublicGalleryItems(value, {
    inventoryMediaEnabled: true,
    experienceMediaEnabled: true,
  });
  if (publicMedia?.state === "empty" || (publishedItems !== null && !publishedItems.length)) {
    return null;
  }
  const candidates = [
    value.publicMedia?.cover,
    value.publicMedia?.primary,
    value.publicGallery?.cover,
    value.canonicalImageDelivery?.gallery?.[0],
    value.destinationMedia?.featured?.main,
    value.inventoryMedia?.featured?.main,
    value.experienceMedia?.featured?.main,
  ].filter(Boolean);
  return candidates.find((candidate) => extractImageDelivery(candidate)) || value;
};

export const compactRecentlyVisitedImageSource = (value) => {
  const canonicalSource = resolveCanonicalRecentSource(value);
  if (!canonicalSource) return null;
  const delivery = extractImageDelivery(canonicalSource);
  if (!delivery) return null;
  const variants = (Array.isArray(delivery.variants) ? delivery.variants : [])
    .filter((variant) => variant?.slotKey === RECENT_IMAGE_SLOT && variant?.path)
    .map((variant) => ({
      slotKey: variant.slotKey,
      role: variant.role || "desktop",
      variantKey: variant.variantKey || "",
      format: variant.format || "webp",
      path: variant.path,
      width: Number(variant.width || 0) || null,
      height: Number(variant.height || 0) || null,
    }));
  const isGalleryCover = delivery.presetKey === "gallery_media";
  const canonicalUrl = isGalleryCover ? resolveImageValue(canonicalSource) : "";
  if (!variants.length && !canonicalUrl) return null;
  return {
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
    metadata: {
      imageOptimization: {
        contractVersion: 1,
        delivery: {
          contractVersion: Number(delivery.contractVersion || 1),
          imageCode: delivery.imageCode || "",
          version: delivery.version || 1,
          presetKey: delivery.presetKey || "entity",
          baseUrl: delivery.baseUrl || "",
          fallbackUrl: "",
          blurUrl: delivery.blurUrl || "",
          thumbnailUrl: delivery.thumbnailUrl || "",
          variants,
        },
      },
    },
  };
};

const normalizeEntry = (entry = {}) => {
  const key = String(entry.key || "").trim();
  if (!key) return null;
  const imageSource = compactRecentlyVisitedImageSource(entry.imageSource || entry.image);
  return {
    key,
    type: String(entry.type || "").trim() || "destination",
    experienceType: String(entry.experienceType || "").trim(),
    identifier: String(entry.identifier || "").trim(),
    title: String(entry.title || "").trim() || "Eco destination",
    subtitle: String(entry.subtitle || "").trim(),
    image:
      imageSource?.metadata?.imageOptimization?.delivery?.thumbnailUrl ||
      resolveImageValue(entry.image),
    imageSource,
    href: String(entry.href || "").trim() || "/stays-camps",
    meta: String(entry.meta || "").trim(),
    visitedAt: Number(entry.visitedAt) || Date.now(),
  };
};

export const readRecentlyVisited = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => normalizeEntry(entry))
      .filter(Boolean)
      .sort((a, b) => b.visitedAt - a.visitedAt);
  } catch (error) {
    return [];
  }
};

export const recordRecentlyVisited = (entry) => {
  if (typeof window === "undefined") return;
  const normalized = normalizeEntry(entry);
  if (!normalized) return;
  const existing = readRecentlyVisited();
  const filtered = existing.filter((item) => item.key !== normalized.key);
  const next = [normalized, ...filtered].slice(0, MAX_ITEMS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    notifyRecentlyVisitedChanged({ action: "record", key: normalized.key });
  } catch (error) {
    // ignore persistence errors
  }
};

export const clearRecentlyVisited = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    notifyRecentlyVisitedChanged({ action: "clear" });
  } catch (error) {
    // ignore persistence errors
  }
};
